// ---- Test Configuration ----

export interface TestConfig {
  chunkSizes: number[];
  maxStreams: number;
  mtu: number;
  syntheticLoss: number;
  jitterMs: number;
  throttleEnabled: boolean;
  throttleRateKB: number;
  serverVersion: string;
}

// ---- Network Tests ----

export interface PingResult {
  seq: number;
  rttMs: number;
  lost: boolean;
  timestamp: number;
}

export interface PingSummary {
  min: number;
  max: number;
  avg: number;
  p50: number;
  p90: number;
  p99: number;
  jitter: number;
  lossPercent: number;
  sent: number;
  received: number;
  rtts: number[];
}

export interface LatencyMeasurement {
  type: 'tcp' | 'tls' | 'dns' | 'http' | 'ws' | 'webrtc';
  durationMs: number;
  timestamp: number;
}

// ---- Throughput ----

export interface SpeedResult {
  speedMbps: number;
  totalBytes: number;
  durationMs: number;
  samples: SpeedSample[];
  burstMbps: number;
  sustainedMbps: number;
}

export interface SpeedSample {
  timestamp: number;
  bytes: number;
  speedMbps: number;
  elapsed: number;
}

// ---- Chunk Analysis ----

export interface ChunkInfo {
  index: number;
  size: number;
  receivedAt: number;
  duration: number;
  gap: number;
  outOfOrder: boolean;
}

export interface ChunkAnalysis {
  chunks: ChunkInfo[];
  avgSize: number;
  avgGap: number;
  outOfOrderCount: number;
  bufferUnderruns: number;
  totalBytes: number;
}

// ---- Stream Diagnostics ----

export interface FlowPoint {
  timestamp: number;
  throughputMbps: number;
  rttMs: number;
  windowSize: number;
}

export interface StreamDiagnostics {
  flowPoints: FlowPoint[];
  congestionDetected: boolean;
  congestionProbability: number;
  rampUpTimeMs: number;
  slowStartPhaseMs: number;
  retransmissionEstimate: number;
  holBlockingDetected: boolean;
  ispShapingDetected: boolean;
  flowStability: number;
}

// ---- Metrics ----

export interface Statistics {
  min: number;
  max: number;
  avg: number;
  median: number;
  p50: number;
  p90: number;
  p95: number;
  p99: number;
  stdDev: number;
  count: number;
}

export interface QualityMetrics {
  mosScore: number;
  flowStability: number;
  bufferHealth: number;
  congestionProbability: number;
  ispShapingDetected: boolean;
  natType: string;
  ipVersion: 'IPv4' | 'IPv6' | 'dual';
  overallGrade: 'A' | 'B' | 'C' | 'D' | 'F';
}

// ---- Client Info ----

export interface ClientInfo {
  ip: string;
  port: string;
  proto: string;
  ipv6: boolean;
  userAgent: string;
  timestamp: number;
  geo?: GeoInfo;
  asn?: ASNInfo;
}

export interface GeoInfo {
  ip: string;
  country: string;
  countryCode: string;
  region: string;
  city: string;
  lat: number;
  lon: number;
  timezone: string;
  isp: string;
  org: string;
}

export interface ASNInfo {
  ip: string;
  asn: number;
  name: string;
  org: string;
}

// ---- Trace ----

export interface TraceResult {
  ip: string;
  geo: GeoInfo | null;
  asn: ASNInfo | null;
  dns: DNSInfo | null;
  ipv6: boolean;
  natType: string;
  hops: HopInfo[];
}

export interface DNSInfo {
  forward: string[];
  reverse: string[];
  resolveMs: number;
}

export interface HopInfo {
  hop: number;
  ip: string;
  rttMs: number;
  host: string;
  loss: boolean;
}

// ---- Full Diagnostic Result ----

export type TestPhase =
  | 'idle'
  | 'connecting'
  | 'info'
  | 'latency'
  | 'download'
  | 'upload'
  | 'analysis'
  | 'complete'
  | 'error';

export interface DiagnosticResult {
  id?: string;
  timestamp: number;
  clientInfo: ClientInfo | null;
  ping: PingSummary | null;
  latencies: LatencyMeasurement[];
  download: SpeedResult | null;
  upload: SpeedResult | null;
  chunkAnalysis: ChunkAnalysis | null;
  streamDiagnostics: StreamDiagnostics | null;
  quality: QualityMetrics | null;
  trace: TraceResult | null;
  phase: TestPhase;
  progress: number;
  error: string | null;
}

export function createEmptyResult(): DiagnosticResult {
  return {
    timestamp: Date.now(),
    clientInfo: null,
    ping: null,
    latencies: [],
    download: null,
    upload: null,
    chunkAnalysis: null,
    streamDiagnostics: null,
    quality: null,
    trace: null,
    phase: 'idle',
    progress: 0,
    error: null,
  };
}

// ---- Video Stream Analysis ----

export type StreamType = 'hls' | 'dash' | 'generic';

export interface HLSVariant {
  bandwidth: number;
  resolution: string;
  codecs: string;
  url: string;
  name?: string;
}

export interface SegmentResult {
  index: number;
  url: string;
  sizeBytes: number;
  ttfbMs: number;
  downloadMs: number;
  speedKbps: number;
  statusCode: number;
  duration?: number;
  error?: string;
}

export interface SegmentStats {
  count: number;
  avgSizeKB: number;
  avgTtfbMs: number;
  avgDownloadMs: number;
  minDownloadMs: number;
  maxDownloadMs: number;
  p90DownloadMs: number;
  avgSpeedKbps: number;
  stdDevMs: number;
  errors: number;
}

export interface StreamAnalysis {
  url: string;
  streamType: StreamType;
  available: boolean;
  statusCode: number;
  contentType: string;
  ttfbMs: number;
  totalTimeMs: number;
  bodySizeBytes: number;
  error?: string;
  variants?: HLSVariant[];
  segments?: SegmentResult[];
  segmentStats?: SegmentStats;
  bitrateKbps: number;
  avgSegDurationMs: number;
  bufferHealthPct: number;
  qualityScore: number;
  serverInfo?: string;
  cdn?: string;
  timestamp: number;
}

export type StreamTestPhase =
  | 'idle'
  | 'connecting'
  | 'parsing_playlist'
  | 'fetching_variant'
  | 'testing_segments'
  | 'complete'
  | 'error';

